import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { AxiosResponse, default as axiosLibrary } from 'axios';

import axios from 'utils/axios';
import { getRefreshToken, setRefreshToken, setToken } from 'utils/localStorage';
import { api } from 'config/api.config'
import useAuthStore from 'models/auth/auth.store';
import { AuthDetails, Credentials, ErrorAuth, LoginDetails } from './auth.types';
import CONSTANTS from 'utils/constants';
import { encrypt } from 'utils/encrypt';
import { initRefresh } from 'utils/refresh-token';
import { ApiError } from 'utils/errors/apiError';
import type { EncryptProps, ToqioResponseProps } from 'utils/types';
import { getLoginErrorMessage } from 'utils/auth';

const headers = {
  Authorization: 'Basic b2xpdm86T2xpdm8yMDE4',
};

export const refreshToken = async (): Promise<string> => {
  try {
    const refreshToken = getRefreshToken();
    const config = `client_id=olivo&refresh_token=${refreshToken}`
    const { data: { access_token, refresh_token } }: AxiosResponse<{ access_token: string, refresh_token: string }> = await axios.post(
      `${api.REFRESH_TOKEN}&${config}`, 
      null,
      { withCredentials: true, headers }
    );
    setToken(access_token);
    setRefreshToken(refresh_token);
    initRefresh();
    return access_token;
  } catch (e) {
    throw new ApiError(CONSTANTS.ERROR_CODES.REFRESH_TOKEN_FAILED);
  }
};

export const useLoginMutation = (): UseMutationResult<AuthDetails, ApiError, Credentials> => {
  return useMutation({
    mutationFn: async (credentails: Credentials) => {
      let encryptedData: EncryptProps;

      try {
        const publicRSAKey: AxiosResponse<ToqioResponseProps<string>> = await axios.get(api.PUBLIC_RSA_KEY);
        if (publicRSAKey) {          
          encryptedData = await encrypt(publicRSAKey?.data, credentails.password);
          const { data: encryptedPassword, iv, encAesKey, tag } = encryptedData;
          
          const config = {
            p: "BACKOFFICE",
            grant_type: "password",
            username: credentails.username,
            password: encryptedPassword,
            iv,
            encAesKey,
            tag,
            tz: "Europe/Paris",
            customerId: process.env.CUSTOMER_ID
          };
          
          const { data }: AxiosResponse<LoginDetails> = await axios.post(
            api.LOGIN,
            null,
            {
              withCredentials: true,
              headers,
              params: config
            }
          );
          setToken(data?.access_token);
          setRefreshToken(data?.refresh_token)
          useAuthStore.getState().login();
          return data;
        }
        else {
          throw new ApiError(CONSTANTS.ERROR_CODES.NO_PUBLIC_KEY);
        }
      } catch (error) {
          if (axiosLibrary.isAxiosError(error)) {
            const responseData = error.response?.data as ErrorAuth;
            const errorMessage = getLoginErrorMessage(responseData?.code);
            throw new ApiError(errorMessage);
          } else {
            throw error;
          }
      }
    }
  })
}